import React, { useRef } from 'react';
import styled from '@emotion/styled';
import css from '@styled-system/css';
import PropTypes from 'prop-types';

import useDirection from '../../hooks/useDirection';
import Flex from '../../layouts/Flex';

const StyledTabs = styled(Flex)(() =>
  css({
    borderRadius: 2,
  })
);

const Tabs = ({
  ariaLabel,
  ariaLabelledBy,
  children: childrenProp,
  onChange,
  selectionFollowsFocus,
  value = 0,
  ...rest
}) => {
  const isRTL = useDirection();
  const tabListRef = useRef(null);

  const children = React.Children.map(childrenProp, (child, index) => {
    if (!React.isValidElement(child)) {
      return null;
    }

    const childValue =
      child.props.value === undefined ? index : child.props.value;

    return React.cloneElement(child, {
      onChange,
      selected: childValue === value,
      selectionFollowsFocus,
      value: childValue,
    });
  });

  const handleKeyDown = (event) => {
    event.persist();
    const { target } = event;
    const role = target.getAttribute('role');

    if (role !== 'tab') {
      return;
    }

    let newFocusTarget = null;
    let previousItemKey = 'ArrowLeft';
    let nextItemKey = 'ArrowRight';
    if (isRTL === 'rtl') {
      // swap previousItemKey with nextItemKey
      previousItemKey = 'ArrowRight';
      nextItemKey = 'ArrowLeft';
    }

    switch (event.key) {
      case previousItemKey:
        newFocusTarget =
          target.previousElementSibling || tabListRef.current.lastChild;
        break;
      case nextItemKey:
        newFocusTarget =
          target.nextElementSibling || tabListRef.current.firstChild;
        break;
      case 'Home':
        newFocusTarget = tabListRef.current.firstChild;
        break;
      case 'End':
        newFocusTarget = tabListRef.current.lastChild;
        break;
      default:
        break;
    }

    if (newFocusTarget !== null) {
      newFocusTarget.focus();
      event.preventDefault();
    }
  };

  return (
    <StyledTabs
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      aria-orientation="horizontal"
      flexDirection="row"
      flexWrap="nowrap"
      justifyContent="space-between"
      onKeyDown={handleKeyDown}
      role="tablist"
      ref={tabListRef}
      {...rest}
    >
      {children}
    </StyledTabs>
  );
};

Tabs.propTypes = {
  /**
   * The label for the Tabs as a string.
   */
  'aria-label': PropTypes.string,
  /**
   * An id or list of ids separated by a space that label the Tabs.
   */
  'aria-labelledby': PropTypes.string,
  /**
   * The content of the component.
   */
  children: PropTypes.node,
  /**
   * Callback fired when the value changes.
   *
   * @param {object} event The event source of the callback. **Warning**: This is a generic event not a change event.
   * @param {any} value We default to the index of the child (number)
   */
  onChange: PropTypes.func,
  /**
   * If `true` the selected tab changes on focus. Otherwise it only
   * changes on activation.
   *
   * https://www.w3.org/TR/wai-aria-practices/#kbd_selection_follows_focus
   */
  selectionFollowsFocus: PropTypes.bool,
  /**
   * The value of the currently selected `Tab`.
   * If you don't want any selected `Tab`, you can set this prop to `false`.
   */
  value: PropTypes.any,
};

export default Tabs;
